import { useEffect, useState } from 'react';

import { disclosureStage, type DisclosureStage } from '@kilat/shared';

import { useAuth } from './use-auth.js';
import { logger } from './logger.js';
import { lifetimeStatsRepoFor } from './repositories/lifetime-stats-repo.js';
import { tenantsRepo } from './repositories/tenants-repo.js';

/**
 * Which capabilities this operator has grown into (gap G20).
 *
 * Reads exactly TWO documents, once: the lifetime counters and the tenant
 * profile. That budget is the reason the counters exist as a maintained
 * document at all — counting customers on demand would be a collection scan on
 * the busiest screen in the product, for a number that changes a few times a
 * day.
 *
 * Fails OPEN. If either read fails we return the last stage, `growing`, which
 * shows everything: a network blip must never make an operator's features
 * appear to vanish. Hiding on uncertainty is the one behaviour this whole
 * feature cannot afford.
 */
export function useDisclosureStage(): DisclosureStage {
  const { claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  // Start at `growing` — everything visible — so the first paint never hides a
  // link and then reveals it, which would read as a glitch.
  const [stage, setStage] = useState<DisclosureStage>('growing');

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    void (async () => {
      try {
        const [stats, tenant] = await Promise.all([
          lifetimeStatsRepoFor(tenantId).get('lifetime'),
          tenantsRepo.get(tenantId),
        ]);
        if (cancelled) return;

        setStage(
          disclosureStage({
            washesRecorded: stats?.washCount ?? 0,
            customersKnown: stats?.customerCount ?? 0,
            // Not read: it would cost a third document for a signal that only
            // brings `retaining` forward slightly, and the customer count gets
            // them there anyway.
            loyaltyConfigured: false,
            showEverything: tenant?.profile.showAllFeatures === true,
          }),
        );
      } catch (e: unknown) {
        logger.warn('disclosure signals unavailable; showing everything', { error: String(e) });
        if (!cancelled) setStage('growing');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return stage;
}
